import React from "react";
import { useParams } from "react-router-dom"; // To get the service name from URL
import { useSiteData } from "@/context/SiteDataContext"; // Import useSiteData hook
import Title from "@/components/shared/Title";
import Button from "@/components/shared/Button";
import ServiceCard from "@/components/shared/ServiceCard";

const SingleServicePage = () => {
  const { serviceName } = useParams(); // Get the service name from the URL params
  const { siteData, loading } = useSiteData(); // Use site data from context

  if (loading) {
    return <p>Loading...</p>; // Show loading indicator if data is still being fetched
  }

  // Find the service data based on the serviceName (URL parameter)
  const services = siteData?.services.slice(0, 4) || [];

  const service = services.find(
    (service) =>
      service.title.toLowerCase().replace(" ", "-") ===
      serviceName.toLowerCase(),
  );

  if (!services) {
    return <p>Service not found</p>; // Handle case where service doesn't exist
  }

  // Helper to get a random color from a list
  const borderColors = [
    "#3D7490",
    "#F59E42",
    "#6C63FF",
    "#FF6B6B",
    "#43B581",
    "#FFD700",
    "#FF69B4",
    "#00BFFF",
    "#8A2BE2",
    "#FF8C00",
    "#FF1493",
    "#00FF7F",
    "#1E90FF",
    "#FF4500",
    "#32CD32",
    "#FF6347",
    "#FF00FF",
    "#00CED1",
    "#FFB6C1",
  ];

  // Function to pick a random color for each card
  const getRandomColor = () =>
    borderColors[Math.floor(Math.random() * borderColors.length)];

  return (
    <div className="flex w-full flex-col items-center justify-between gap-4 px-4 md:flex-row md:px-12 xl:px-16">
      <div className="p-8">
        <Title title="Service Details" className="mb-10" />

        {/* Service Header */}
        <div className="mb-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-2">
          <img
            src={service.image || ""}
            alt={service.title}
            className="mb-6 max-h-[364px] w-full rounded-lg object-cover"
          />
          <div>
            <h1 className="mb-4 text-4xl font-semibold">{service.title}</h1>
            <p className="text-gray mb-8 text-2xl">{service.description}</p>
            <div className="mb-8 flex gap-4">
              <div className="ring-gray/40 w-fit rounded-2xl p-2.5 text-base font-medium ring">
                <span className="text-primary text-xl">10K</span> Happy Clients
              </div>
              <div className="ring-gray/40 w-fit rounded-2xl p-2.5 text-base font-medium ring">
                <span className="text-primary text-xl">300+</span> Positive
                Reviews
              </div>
            </div>
            <Button
              text="Book Now"
              blank
              className="!px-22"
              url={service.url}
            />
          </div>
        </div>

        {/* Service Benefits Section */}
        <div className="benefits-section mb-12">
          <Title
            title="What You Will Get ?"
            className="mb-10 text-2xl font-semibold"
          />

          <div className="grid grid-cols-1 gap-x-24 gap-y-12 sm:grid-cols-2">
            {service.benefits.map((benefit, index) => (
              <div
                key={index}
                className="ring-gray/40 rounded-lg p-4 shadow-sm ring"
                style={{
                  borderLeft: `4px solid ${getRandomColor()}`,
                }}
              >
                <div className="flex items-center gap-4">
                  <benefit.icon className="text-primary text-4xl xl:text-[48px]" />
                  <h3 className="text-2xl font-medium xl:text-[32px]">
                    {benefit.title}
                  </h3>
                </div>
                <p className="text-gray mt-2 text-xl">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Explore Services Section */}
        <div className="benefits-section mb-6 xl:mb-12">
          <Title
            title="Explore More Services"
            className="mb-10 text-2xl font-semibold"
          />

          <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-4">
            {services.map((service, index) => (
              <ServiceCard key={index} service={service} /> // Using the ServiceCard component
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleServicePage;
